import { useQC } from './hooks/useQC';

export default function QCDashboard() {
  const { dashboard } = useQC();
  
  if (dashboard.isLoading) return <div className="p-6">Loading...</div>;
  
  const d = dashboard.data;
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">QC Dashboard</h1>
        <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleTimeString()}
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="card bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-blue-600 font-medium">Pending Inspections</div>
              <div className="text-2xl font-bold text-blue-900">{d?.stats.pending}</div>
            </div>
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-white text-xl"></i>
            </div>
          </div>
        </div>
        
        <div className="card bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-green-600 font-medium">Completed Today</div>
              <div className="text-2xl font-bold text-green-900">{d?.stats.todayCompleted}</div>
            </div>
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <i className="ri-check-line text-white text-xl"></i>
            </div>
          </div>
        </div>
        
        <div className="card bg-gradient-to-r from-red-50 to-red-100 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-red-600 font-medium">Rejected</div>
              <div className="text-2xl font-bold text-red-900">{d?.stats.rejected}</div>
            </div>
            <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
              <i className="ri-close-line text-white text-xl"></i>
            </div>
          </div>
        </div>
        
        <div className="card bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-purple-600 font-medium">Approval Rate</div>
              <div className="text-2xl font-bold text-purple-900">{d?.stats.approvalRate}%</div>
            </div>
            <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
              <i className="ri-award-line text-white text-xl"></i>
            </div>
          </div>
        </div>
      </div>
      
      {/* Performance Metrics */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <i className="ri-bar-chart-line text-[var(--color-primary)] mr-2"></i>
            Performance Metrics
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Inspection Time</span>
              <span className="font-semibold">{d?.stats.avgInspectionTime} min</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Completed</span>
              <span className="font-semibold">{d?.stats.completed}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Quality Score</span>
              <span className="font-semibold text-green-600">Excellent</span>
            </div>
          </div>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <i className="ri-history-line text-[var(--color-primary)] mr-2"></i>
            Recent QC Actions
          </h3>
          <div className="space-y-3">
            {d?.recent.map((r: any, index: number) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                <div className="flex-1">
                  <div className="font-medium text-sm">{r.orderId}</div>
                  <div className="text-xs text-gray-500">{r.product}</div>
                </div>
                <div className="text-right">
                  <span className={`badge ${r.status === 'approved' ? 'approved' : r.status === 'rejected' ? 'suspended' : 'pending'}`}>
                    {r.status}
                  </span>
                  <div className="text-xs text-gray-500 mt-1">{r.timestamp}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <i className="ri-flashlight-line text-[var(--color-primary)] mr-2"></i>
          Quick Actions
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          <button className="btn-primary flex items-center justify-center">
            <i className="ri-search-line mr-2"></i>
            View Pending Orders
          </button>
          <button className="btn-secondary flex items-center justify-center">
            <i className="ri-file-list-line mr-2"></i>
            Manage Checklists
          </button>
          <button className="btn-secondary flex items-center justify-center">
            <i className="ri-download-line mr-2"></i>
            Export Reports
          </button>
        </div>
      </div>
    </div>
  );
}